import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Group,
  TextInput,
  Select,
  Table,
  Text,
  Badge,
  Avatar,
  ActionIcon,
  Tooltip,
  LoadingOverlay,
  Button,
  Image,
  Alert,
  Paper,
  Pagination
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useAuthInfo } from '@propelauth/react';
import searchIcon from '../../assets/icons/search.svg';
import editIcon from '../../assets/icons/edit.svg';
import commentIcon from '../../assets/icons/comment.svg';
import receiptIcon from '../../assets/icons/receipt.svg';
import trashIcon from '../../assets/icons/trash.svg';
import { env } from '../../utils/env';
import ExpenseDetail from './ExpenseDetail';
import { notifications } from '@mantine/notifications';
import { AsyncImage } from '../../utils/imageUtils';
import { GroupExpenseItem, PaginatedGroupExpenseResponse } from '../../types/group';

interface ExpenseListProps {
  groupId: number;
  onSelect: (expense: GroupExpenseItem) => void;
  accessToken: string;
  onDataChange?: () => void;
  refreshTrigger?: number;
}

export const ExpenseList = ({ groupId, onSelect, accessToken, onDataChange, refreshTrigger = 0 }: ExpenseListProps) => {
  const { user } = useAuthInfo();
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [expenses, setExpenses] = useState<GroupExpenseItem[]>([]);
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<GroupExpenseItem | null>(null);
  const [showExpenseDetail, setShowExpenseDetail] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalPages, setTotalPages] = useState<number>(0);

  useEffect(() => {
    const fetchExpenses = async (pageToFetch: number, sizeToFetch: number) => {
      try {
        setLoading(true);

        const queryParams = new URLSearchParams();
        queryParams.append('page', pageToFetch.toString());
        queryParams.append('size', sizeToFetch.toString());

        if (categoryId) {
          queryParams.append('category_id', categoryId);
        }
        if (dateRange[0]) {
          queryParams.append('start_date', dateRange[0].toISOString().split('T')[0]);
        }
        if (dateRange[1]) {
          queryParams.append('end_date', dateRange[1].toISOString().split('T')[0]);
        }
        if (searchQuery) {
          queryParams.append('merchant_name', searchQuery);
        }

        console.log('Fetching expenses with params:', queryParams.toString());

        const response = await fetch(
          `${env.API_URL}/api/groups/${groupId}/expenses?${queryParams.toString()}`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`Failed to fetch expenses: ${response.status} - ${errorText}`);
        }

        const data: PaginatedGroupExpenseResponse = await response.json();

        setExpenses(data.items);
        setCurrentPage(data.page);
        setPageSize(data.size);
        setTotalPages(data.pages);

        if (data.page === 1) {
          try {
            const categoriesResponse = await fetch(
              `${env.API_URL}/api/groups/${groupId}/category`,
              {
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                },
              }
            );
            if (categoriesResponse.ok) {
              const categoriesData = await categoriesResponse.json();
              setCategories(Array.isArray(categoriesData) ? categoriesData : []);
            }
          } catch (err) {
            console.error('Error fetching categories:', err);
          }
        }
      } catch (err) {
        console.error('Error fetching expenses:', err);
        setError(err instanceof Error ? err.message : 'Failed to load expenses');
      } finally {
        setLoading(false);
      }
    };

    fetchExpenses(currentPage, pageSize);
  }, [groupId, accessToken, currentPage, pageSize, searchQuery, dateRange, categoryId, refreshTrigger]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleExpenseClick = (expense: GroupExpenseItem) => {
    setSelectedExpense(expense);
    setShowExpenseDetail(true);
  };

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      const response = await fetch(
        `${env.API_URL}/api/groups/${groupId}/expenses/${expenseId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete expense');
      }

      // Refresh expenses list
      setCurrentPage(1);
      // The fetchExpenses effect will trigger automatically

      // Notify parent component to refresh other related data (like balances)
      if (onDataChange) {
        onDataChange();
      }
    } catch (err) {
      console.error('Error deleting expense:', err);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete expense',
        color: 'red',
      });
    }
  };

  if (error) {
    return (
      <Alert color="red" title="Error loading expenses">
        {error}
      </Alert>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={loading} />

      <Stack gap="md">
        <Group>
          <TextInput
            placeholder="Search by merchant name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            leftSection={<img src={searchIcon} alt="Search" width={16} />}
            style={{ flex: 1 }}
          />

          <DatePickerInput
            type="range"
            placeholder="Filter by date"
            value={dateRange}
            onChange={setDateRange}
            clearable
          />

          <Select
            placeholder="Category"
            value={categoryId}
            onChange={setCategoryId}
            data={categories.map(c => ({ value: c.id.toString(), label: c.name }))}
            clearable
          />
        </Group>

        {expenses.length === 0 && !loading ? (
          <Paper p="xl" withBorder>
            <Text c="dimmed" ta="center" py="xl">
              No expenses found. Add your first expense to get started!
            </Text>
          </Paper>
        ) : (
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Paid By</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Amount</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {expenses.map(expense => (
                <Table.Tr
                  key={expense.id}
                  onClick={() => handleExpenseClick(expense)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td>
                    <Group gap="xs">
                      <Text size="sm">
                        {new Date(expense.date).toLocaleDateString()}
                      </Text>
                      {expense.is_recurring && (
                        <Tooltip label="Recurring expense">
                          <Badge size="xs">Recurring</Badge>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>

                  <Table.Td>
                    <Group gap="xs">
                      <Text size="sm">{expense.title}</Text>
                      {expense.receipt_url && (
                        <img src={receiptIcon} alt="Has receipt" width={16} />
                      )}
                      {expense.expense_image_url && (
                        <div style={{ position: 'relative', width: 40, height: 40 }}>
                          <AsyncImage
                            src={(expense.thumbnail_url || expense.expense_image_url)?.startsWith('http')
                              ? (expense.thumbnail_url || expense.expense_image_url)
                              : `${env.R2_PUBLIC_URL}/${expense.thumbnail_url || expense.expense_image_url}`}
                            alt="Expense thumbnail"
                            width={40}
                            height={40}
                            fit="cover"
                            radius="sm"
                            style={{ cursor: 'pointer' }}
                            loadingHeight={40}
                          />
                        </div>
                      )}
                      {(expense.comment_count ?? 0) > 0 && (
                        <Badge size="xs" leftSection={
                          <img src={commentIcon} alt="" width={12} />
                        }>
                          {expense.comment_count}
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>

                  <Table.Td>
                    <Group gap="xs">
                      <Avatar
                        src={expense.paid_by.avatar_url}
                        size="sm"
                        radius="xl"
                      >
                        {expense.paid_by.display_name ? expense.paid_by.display_name.charAt(0) : expense.paid_by.email.charAt(0)}
                      </Avatar>
                      <Text size="sm">{expense.paid_by.display_name || expense.paid_by.email}</Text>
                    </Group>
                  </Table.Td>

                  <Table.Td>
                    <Badge>{expense.category?.name || 'Uncategorized'}</Badge>
                  </Table.Td>

                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {formatCurrency(expense.amount, expense.currency_code)}
                    </Text>
                  </Table.Td>

                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExpenseClick(expense);
                        }}
                      >
                        <img src={editIcon} alt="Edit" width={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Are you sure you want to delete this expense?')) {
                            handleDeleteExpense(expense.id.toString());
                          }
                        }}
                      >
                        <img src={trashIcon} alt="Delete" width={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        {totalPages > 1 && (
          <Group justify="center" mt="md">
            <Pagination total={totalPages} value={currentPage} onChange={handlePageChange} />
          </Group>
        )}
      </Stack>

      {selectedExpense && showExpenseDetail && (
        <ExpenseDetail
          expenseId={selectedExpense.id.toString()}
          groupId={groupId.toString()}
          isOpen={showExpenseDetail}
          onClose={() => {
            setShowExpenseDetail(false);
            setSelectedExpense(null);
          }}
          onEdit={(expense) => {
            onSelect(expense);
            setShowExpenseDetail(false);
          }}
          onDelete={handleDeleteExpense}
          accessToken={accessToken}
          current_user={{
            user_id: user?.userId || '',
            picture_url: user?.pictureUrl || '',
            email: user?.email || ''
          }}
        />
      )}
    </div>
  );
}; 